import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronDown, ChevronRight, Star, Search } from "lucide-react";
import { Account } from "@blinkbooks/types";
import apiClient from "@/utils/httpClient";
import { fetchFinancialStructureSummary, FinancialStructureSummary } from "@/services/kpi.service";
import { AccountRow, SubAccountRow } from "./interfaces";
import AccountDetailSheet from "./AccountDetailSheet";
import { formatAmount } from "@/utils/expenseFormatters";
import {
  getBlinkBooksCategory,
  getAccountSection,
  isKeyAccount,
  getStructuralRole,
  getCategoryStyle,
  AccountSection,
} from "./utils";

const PL_SECTIONS = new Set<AccountSection>(["Revenue", "COGS", "Operating Expenses"]);

const STRUCTURE: { statement: string; section: AccountSection }[] = [
  { statement: "BALANCE SHEET", section: "Assets" },
  { statement: "BALANCE SHEET", section: "Liabilities" },
  { statement: "BALANCE SHEET", section: "Equity" },
  { statement: "PROFIT & LOSS",  section: "Revenue" },
  { statement: "PROFIT & LOSS",  section: "COGS" },
  { statement: "PROFIT & LOSS",  section: "Operating Expenses" },
];

// Sections where balances represent money owed (shown as positive but labeled "Owed")
const OWED_SECTIONS = new Set<AccountSection>(["Liabilities"]);

const Accounting = () => {
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [summary, setSummary] = useState<FinancialStructureSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showZeroBalance, setShowZeroBalance] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<AccountRow | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [collapsedSections, setCollapsedSections] = useState<Set<AccountSection>>(
    new Set(["Revenue", "COGS", "Operating Expenses"])
  );
  const [showZeroInSections, setShowZeroInSections] = useState<Set<AccountSection>>(new Set());

  useEffect(() => {
    fetchAccounts();
    fetchFinancialStructureSummary()
      .then(setSummary)
      .catch(() => setSummary(null));
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<Account[]>("/accounts");
      setAccounts(data.map((acc) => ({ ...acc })));
    } catch (error) {
      console.error("Error fetching accounts:", error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  };

  const toggleSection = (section: AccountSection) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) { next.delete(section); } else { next.add(section); }
      return next;
    });
  };

  const cashTotal = summary?.totalCash ?? 0;
  const receivablesTotal = summary?.customerReceivables ?? 0;
  const payablesTotal = summary?.vendorPayables ?? 0;

  const matchesSearch = (name?: string | null, num?: string | null) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (name?.toLowerCase().includes(q) ?? false) || (num?.toLowerCase().includes(q) ?? false);
  };

  const getBySection = (section: AccountSection, includeZero = false): AccountRow[] =>
    accounts.filter((acc) => {
      if (!matchesSearch(acc.name, acc.accountNumber)) return false;
      if (getAccountSection(acc.type ?? "", acc.name) !== section) return false;
      if (!showZeroBalance && !includeZero && !showZeroInSections.has(section) && (acc.balance ?? 0) === 0) return false;
      return true;
    });

  const hiddenZeroCount = (section: AccountSection): number => {
    if (showZeroBalance || showZeroInSections.has(section)) return 0;
    return accounts.filter(
      (acc) =>
        getAccountSection(acc.type ?? "", acc.name) === section &&
        matchesSearch(acc.name, acc.accountNumber) &&
        (acc.balance ?? 0) === 0
    ).length;
  };

  const renderSubRows = (subs: SubAccountRow[], level: number, isOwed: boolean): JSX.Element[] =>
    subs.flatMap((sub) => {
      const category = getBlinkBooksCategory(sub.type ?? "", sub.name);
      const catStyle = getCategoryStyle(category);
      const role = getStructuralRole(sub.name ?? "", sub.type ?? "");
      const isExpanded = expandedIds.has(sub.id ?? "");
      const hasSubs = (sub.subAccounts?.length ?? 0) > 0;
      const displayBalance = isOwed ? Math.abs(sub.balance ?? 0) : (sub.balance ?? 0);

      return [
        <tr
          key={`sub-${sub.id}-${level}`}
          className="border-b border-border/20 hover:bg-muted/10 transition-colors"
        >
          <td className="py-2 px-3" style={{ paddingLeft: `${level * 1.75 + 0.75}rem` }}>
            {hasSubs ? (
              <button
                className="flex items-center justify-center w-5 h-5 text-muted-foreground/40 hover:text-muted-foreground"
                onClick={(e) => { e.stopPropagation(); toggleExpand(sub.id ?? ""); }}
              >
                {isExpanded
                  ? <ChevronDown className="h-3.5 w-3.5" />
                  : <ChevronRight className="h-3.5 w-3.5" />}
              </button>
            ) : (
              <span className="text-muted-foreground/30 text-xs pl-1">└</span>
            )}
          </td>
          <td className="py-2 px-1" />
          <td className="py-2 px-3">
            <button
              className="text-sm text-foreground/80 hover:text-primary hover:underline text-left"
              onClick={() => setSelectedAccount(sub as AccountRow)}
            >
              {sub.name}
            </button>
          </td>
          <td className="py-2 px-3">
            <span
              title={sub.type ?? ""}
              className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${catStyle.bg} ${catStyle.text} ${catStyle.border}`}
            >
              {category}
            </span>
          </td>
          <td className="py-2 px-3">
            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${role.bg} ${role.text} ${role.border}`}>
              {role.label}
            </span>
          </td>
          <td className="py-2 px-3 text-right text-sm font-medium tabular-nums text-foreground/80">
            {sub.balance !== undefined ? formatAmount(displayBalance) : "—"}
          </td>
        </tr>,
        ...(isExpanded && hasSubs ? renderSubRows(sub.subAccounts!, level + 1, isOwed) : []),
      ];
    });

  const renderAccountRows = (sectionAccounts: AccountRow[], isOwed: boolean): JSX.Element[] =>
    sectionAccounts.flatMap((account) => {
      const category = getBlinkBooksCategory(account.type ?? "", account.name);
      const catStyle = getCategoryStyle(category);
      const isKey = isKeyAccount(account.name ?? "", account.type ?? "");
      const role = getStructuralRole(account.name ?? "", account.type ?? "");
      const isExpanded = expandedIds.has(account.id ?? "");
      const hasSubs = (account.subAccounts?.length ?? 0) > 0;
      const displayBalance = isOwed ? Math.abs(account.balance ?? 0) : (account.balance ?? 0);

      return [
        <tr
          key={account.id}
          className={`border-b border-border/20 hover:bg-muted/20 transition-colors${hasSubs ? " cursor-pointer" : ""}`}
          onClick={() => hasSubs && toggleExpand(account.id ?? "")}
        >
          <td className="py-2.5 px-3 w-10">
            {hasSubs ? (
              isExpanded
                ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
                : <ChevronRight className="h-4 w-4 text-muted-foreground" />
            ) : <div className="w-4" />}
          </td>
          <td className="py-2.5 px-1 w-6">
            {isKey && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400 cursor-default" />
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p className="text-xs">Key account — directly impacts cash, revenue, or payroll</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </td>
          <td className="py-2.5 px-3">
            <button
              className="font-semibold text-sm text-foreground hover:text-primary hover:underline text-left"
              onClick={(e) => { e.stopPropagation(); setSelectedAccount(account); }}
            >
              {account.name}
            </button>
          </td>
          <td className="py-2.5 px-3">
            <span
              title={account.type ?? ""}
              className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${catStyle.bg} ${catStyle.text} ${catStyle.border}`}
            >
              {category}
            </span>
          </td>
          <td className="py-2.5 px-3">
            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${role.bg} ${role.text} ${role.border}`}>
              {role.label}
            </span>
          </td>
          <td className="py-2.5 px-3 text-right text-sm font-semibold tabular-nums text-foreground">
            {account.balance !== undefined ? formatAmount(displayBalance) : "—"}
          </td>
        </tr>,
        ...(isExpanded && hasSubs ? renderSubRows(account.subAccounts!, 1, isOwed) : []),
      ];
    });

  const buildTableBody = (): JSX.Element[] => {
    const rows: JSX.Element[] = [];
    let lastStatement = "";

    for (const { statement, section } of STRUCTURE) {
      const sectionAccounts = getBySection(section);
      const isOwed = OWED_SECTIONS.has(section);
      const sectionTotal = sectionAccounts.reduce((s, a) => s + Math.abs(a.balance ?? 0), 0);
      const isCollapsed = collapsedSections.has(section);

      if (statement !== lastStatement) {
        lastStatement = statement;
        rows.push(
          <tr key={`stmt-${statement}`} className="bg-muted/5">
            <td colSpan={6} className="py-4 px-4">
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border/60" />
                <span className="text-xs font-bold uppercase tracking-widest text-foreground/50 px-2">
                  {statement}
                </span>
                <div className="h-px flex-1 bg-border/60" />
              </div>
            </td>
          </tr>
        );
      }

      rows.push(
        <tr
          key={`sec-${section}`}
          className="bg-muted/30 cursor-pointer hover:bg-muted/40 transition-colors select-none"
          onClick={() => toggleSection(section)}
        >
          <td colSpan={4} className="py-2 px-12">
            <div className="flex items-center gap-2">
              {isCollapsed
                ? <ChevronRight className="h-3 w-3 text-muted-foreground/60" />
                : <ChevronDown className="h-3 w-3 text-muted-foreground/60" />}
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {section}
              </span>
              {isCollapsed && sectionAccounts.length > 0 && (
                <span className="text-xs text-muted-foreground/50">
                  ({sectionAccounts.length})
                </span>
              )}
            </div>
          </td>
          <td className="py-2 px-3 text-right" colSpan={2}>
            <div className="flex items-center justify-end gap-2">
              {isOwed && (
                <span className="text-xs text-orange-500 font-medium">owed</span>
              )}
              <span className="text-xs font-semibold tabular-nums text-muted-foreground">
                {formatAmount(sectionTotal)}
              </span>
            </div>
          </td>
        </tr>
      );

      if (!isCollapsed) {
        const hidden = hiddenZeroCount(section);

        if (sectionAccounts.length === 0 && hidden === 0) {
          rows.push(
            <tr key={`empty-${section}`}>
              <td colSpan={6} className="py-3 px-14 text-xs text-muted-foreground/50 italic">
                No accounts
              </td>
            </tr>
          );
        } else {
          rows.push(...renderAccountRows(sectionAccounts, isOwed));
        }

        if (hidden > 0) {
          rows.push(
            <tr key={`hidden-zero-${section}`} className="border-b border-border/10">
              <td colSpan={6} className="py-2 px-14">
                <button
                  className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                  onClick={() =>
                    setShowZeroInSections((prev) => {
                      const next = new Set(prev);
                      next.add(section);
                      return next;
                    })
                  }
                >
                  {hidden} account{hidden !== 1 ? "s" : ""} with $0 balance hidden — show
                </button>
              </td>
            </tr>
          );
        }
      }
    }
    return rows;
  };

  return (
    <Card className="m-6 shadow-md">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-1">
            <CardTitle>Financial Structure</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Underlying accounts and balances powering your Financial Control Panel, synced from QuickBooks.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search accounts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-8 w-52 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="show-zero"
                checked={showZeroBalance}
                onCheckedChange={setShowZeroBalance}
              />
              <Label
                htmlFor="show-zero"
                className="text-sm text-muted-foreground cursor-pointer whitespace-nowrap"
              >
                Show zero-balance
              </Label>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        {/* Technical rollup cards — match the Control Panel primitives */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Total Cash</p>
            <p className="text-2xl font-bold tabular-nums text-emerald-600">{formatAmount(cashTotal)}</p>
            <p className="text-xs text-muted-foreground mt-1">Connected bank accounts · excl. credit cards · All time</p>
          </div>
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Customer Receivables</p>
            <p className="text-2xl font-bold tabular-nums text-sky-600">{formatAmount(receivablesTotal)}</p>
            <p className="text-xs text-muted-foreground mt-1">Sum of open invoice balances · All time</p>
          </div>
          <div className="rounded-xl border bg-card p-4 shadow-sm">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Vendor Payables</p>
            <p className="text-2xl font-bold tabular-nums text-orange-600">{formatAmount(payablesTotal)}</p>
            <p className="text-xs text-muted-foreground mt-1">Sum of unpaid bill balances · All time</p>
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm">Loading accounts...</span>
          </div>
        ) : (
          <div className="rounded-lg border border-border/40 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40">
                  <th className="w-10 py-3 px-3" />
                  <th className="w-6 py-3 px-1" />
                  <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Account
                  </th>
                  <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Category
                  </th>
                  <th className="text-left py-3 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Role
                  </th>
                  <th className="text-right py-3 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Balance
                  </th>
                </tr>
              </thead>
              <tbody>{buildTableBody()}</tbody>
            </table>
          </div>
        )}
      </CardContent>

      <AccountDetailSheet
        account={selectedAccount}
        onClose={() => setSelectedAccount(null)}
      />

      {/* Roadmap preview — coming layers */}
      <div className="px-6 pb-6">
        <div className="rounded-xl border border-dashed border-border/40 bg-muted/10 p-5 select-none">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground/40 mb-4">
            Coming next
          </p>
          <div className="grid grid-cols-3 gap-4">
            {[
              {
                label: "Account Explorer",
                desc: "Click any account to see the full ledger — every transaction, direction, and running balance behind the number.",
              },
              {
                label: "Transaction Intelligence",
                desc: "Click any transaction to trace it: invoice → payment → bank deposit → QuickBooks record. Full chain, one click.",
              },
              {
                label: "AI Explanation Layer",
                desc: "Ask why a number changed. AI answers with actual drivers: which vendors, which categories, which dates.",
              },
            ].map(({ label, desc }) => (
              <div key={label} className="space-y-1.5">
                <p className="text-sm font-medium text-muted-foreground/40">{label}</p>
                <p className="text-xs text-muted-foreground/30 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
};

export default Accounting;
